import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PreviewTab from './PreviewTab';
import ReviewsTab from './ReviewsTab';
import InformationTab from './InformationTab';
import SparkPopupHeader from './SparkPopupHeader';
import { CreateFavouriteListFormValues } from 'components/common/Favourite/CreateFavouriteListPopUp';
import { SparkButton, SparkDialog, SparkTabs } from 'sharedComponents/UI';
import { EditIcon, Star02 } from 'components/SVG';
import { TabItem } from 'sharedComponents/UI/SparkTabs';
import { useCurrentUser } from 'hooks/common/useCurrentUser';
import { useAddUserFlag } from 'hooks/userFlags/useAddUserFlag';
import { useFlagReasons } from 'hooks/EntityTypes/useFlagReasons';
import {
  useCreateFavouriteList,
  useCreateSparkFavourite,
  useDeleteSparkFavourite,
} from 'hooks/Favourites';
import {
  useAddSparkRating,
  useSparksByID,
  useUpdateSpark,
  useUpdateSparkRating,
} from 'hooks/spark';
import { useSparkPrivacyTypes } from 'hooks/EntityTypes/useSparkPrivacyTypes';
import { SPARK_ROUTES } from 'constants/routes';
import type { SparkReportFormValues } from 'components/common/Report/SparkReport';
import type { ReviewFormModalValues } from 'components/common/ReviewFormModal';
import RunsTab from './RunsTab';
import SparkBadges from 'components/common/SparkBadges';
import { useCurrentRoute, useResize } from 'hooks/common';
import { isNullOrUndefined } from 'utilities/index';
import { getGropedSparkBadges } from 'helpers/groupSparkBadges';
import SparkPopupHeaderSkeleton from 'sharedComponents/Skeleton/SparkPopupHeaderSkeleton';
import CardContentSkeleton from 'sharedComponents/Skeleton/CardContentSkeleton';
import { getCurrentDateISOString } from 'utilities/time';
type CreateSparkModalProps = {
  isSparkRunDialogOpen: boolean;
  closeSparkRunDialog: () => void;
  sparkID: string | undefined;
};

function RunSparkModal({
  isSparkRunDialogOpen,
  closeSparkRunDialog,
  sparkID,
}: CreateSparkModalProps) {
  const {
    isFetching: isSparkFetching,
    joinedSpark,
    isLoading: isLoadingSpark,
  } = useSparksByID(sparkID);
  const navigate = useNavigate();
  const { isMobile } = useResize();
  const { currentUserID } = useCurrentUser();
  const { sparkPrivacyTypeIdByNameMap } = useSparkPrivacyTypes();
  const { mutateAsync: updateSpark } = useUpdateSpark(sparkID);
  const { isSparkRun } = useCurrentRoute();

  const {
    mutateAsync: createSparkFavorites,
    isLoading: isCreatingSparkFavourite,
  } = useCreateSparkFavourite();

  const {
    mutateAsync: deleteSparkFavorites,
    isLoading: isDeletingSparkFavourite,
  } = useDeleteSparkFavourite();

  const { mutateAsync: addUserFlag } = useAddUserFlag();
  const { mutateAsync: createFavoriteList } = useCreateFavouriteList();
  const { dropdownOptions: flagReasonsDropdownOptions } = useFlagReasons();

  const existingFavouriteList = joinedSpark?.meta.user.favouriteLists;

  const HandleTabClicked = () => {
    closeSparkRunDialog();
  };

  const isSparkPublic = React.useMemo(() => {
    if (!joinedSpark || !sparkPrivacyTypeIdByNameMap) {
      return false;
    }
    return (
      joinedSpark.attributes.sparkPrivacyTypeID ===
      +sparkPrivacyTypeIdByNameMap['Public']
    );
  }, [joinedSpark, sparkPrivacyTypeIdByNameMap]);

  const tabItems: TabItem[] = React.useMemo(() => {
    if (isNullOrUndefined(isSparkPublic)) {
      return [];
    }
    return [
      {
        label: 'Overview',
        content: <PreviewTab joinedSpark={joinedSpark} />,
      },

      ...(currentUserID
        ? [
            {
              label: 'Runs',
              content: (
                <RunsTab sparkID={sparkID} onTabClick={HandleTabClicked} />
              ),
            },
          ]
        : []),
      ...(joinedSpark
        ? [
            {
              label: 'Reviews',
              content: <ReviewsTab spark={joinedSpark} />,
            },
          ]
        : []),
      {
        label: 'Information',
        content: <InformationTab joinedSpark={joinedSpark} />,
      },
    ];
  }, [isSparkPublic, joinedSpark, sparkID]);
  const handleCreateList = async (values: CreateFavouriteListFormValues) => {
    const res = await createFavoriteList(values.listName);

    const sparkFavourite = {
      sparkID: sparkID,
      userID: currentUserID,
      favouriteListID: Number(res.data.id),
    };
    await createSparkFavorites(sparkFavourite);
  };
  const { ratings, permissions } = joinedSpark?.meta.user || {};
  const allowedToReview = permissions?.canReview;
  const userRating = ratings?.find((rating) => rating.sparkID === sparkID);

  const { mutateAsync: addSparkRating } = useAddSparkRating();
  const { mutateAsync: updateSparkRating } = useUpdateSparkRating(
    userRating?.id ? `${userRating?.id}` : undefined
  );
  const handleFavouriteListItem = async (listID: number) => {
    if (existingFavouriteList?.some((listItem) => listItem.id === listID)) {
      const sparkFavourite = joinedSpark?.sparkFavourites?.find(
        (favourite) => favourite.attributes.favouriteListID === listID
      );

      await deleteSparkFavorites(sparkFavourite?.id || '');
      return;
    }
    const sparkFavourite = {
      sparkID: sparkID,
      userID: currentUserID,
      favouriteListID: listID,
    };
    await createSparkFavorites(sparkFavourite);
  };

  const handleSparkRating = async (values: ReviewFormModalValues) => {
    const doesReviewExists = !!userRating;
    if (!doesReviewExists) {
      await addSparkRating({ ...values, sparkID: sparkID });
    }
    if (doesReviewExists) {
      await updateSparkRating({ ...values, sparkID: sparkID });
    }
  };

  const handleSendReport = async (values: SparkReportFormValues) => {
    const userFlag = {
      flagReasonID: +values.reasonID,
      sparkID: sparkID,
      comments: values.comments,
    };
    await addUserFlag(userFlag);
  };

  const handleDeleteSpark = async () => {
    await updateSpark({
      deletedAt: getCurrentDateISOString(),
      isDeleted: true,
    });
  };

  const handleArchiveSpark = async () => {
    await updateSpark({
      isArchived: true,
    });
  };
  const userPermissions = joinedSpark?.meta.user.permissions;

  const isLoadingSparkFavourites =
    isCreatingSparkFavourite || isDeletingSparkFavourite || isSparkFetching;
  const badgesCategoryMap = getGropedSparkBadges(joinedSpark?.meta?.badges);

  return (
    <SparkDialog
      open={isSparkRunDialogOpen}
      onClose={closeSparkRunDialog}
      title={
        isMobile ? (
          <SparkPopupHeader
            joinedSpark={joinedSpark}
            onCreateList={handleCreateList}
            onFavouriteItemClick={handleFavouriteListItem}
            existingFavouriteList={existingFavouriteList || []}
            isLoading={isLoadingSparkFavourites}
            flagReasonsDropdownOptions={flagReasonsDropdownOptions}
            handleSendReport={handleSendReport}
            allowedToReview={!!allowedToReview}
            handleSparkRatingSave={handleSparkRating}
            userRating={userRating}
            onArchive={handleArchiveSpark}
            userPermissions={userPermissions}
            onDelete={handleDeleteSpark}
          />
        ) : (
          <></>
        )
      }
      sparkDialogHeader={
        isLoadingSpark ? (
          <SparkPopupHeaderSkeleton />
        ) : !isMobile ? (
          <SparkPopupHeader
            joinedSpark={joinedSpark}
            onCreateList={handleCreateList}
            onFavouriteItemClick={handleFavouriteListItem}
            existingFavouriteList={existingFavouriteList || []}
            isLoading={isLoadingSparkFavourites}
            flagReasonsDropdownOptions={flagReasonsDropdownOptions}
            handleSendReport={handleSendReport}
            allowedToReview={!!allowedToReview}
            handleSparkRatingSave={handleSparkRating}
            userRating={userRating}
            onDelete={handleDeleteSpark}
            onArchive={handleArchiveSpark}
            userPermissions={userPermissions}
          />
        ) : (
          <></>
        )
      }
      titleContainerStyle={{ height: isMobile ? 'auto' : '2rem' }}
      sx={(theme) => ({
        '& .MuiDialogContent-root': {
          padding: 4,
          paddingTop: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          [theme.breakpoints.down(1000)]: {
            padding: 2,
          },
        },
      })}
    >
      {isLoadingSpark ? (
        <CardContentSkeleton />
      ) : (
        <Box>
          <Box
            marginBottom={2}
            display='flex'
            flexDirection='column'
            paddingTop={'10px'}
          >
            <SparkBadges badges={badgesCategoryMap?.['Info'] || []} />
            <Typography
              sx={{
                fontSize: '16px',
                fontStyle: 'normal',
                borderRadius: '6px',
                whiteSpace: 'pre-line',
                padding: 1,
              }}
            >
              {joinedSpark?.attributes.descriptionforHuman}
            </Typography>
          </Box>
          <SparkTabs
            tabItems={tabItems}
            height='50vh'
            tabPanelStyles={{ paddingY: 1 }}
          />
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            gap={2}
            paddingTop={2}
          >
            {joinedSpark?.meta.user.permissions?.canUpdate && (
              <SparkButton
                variant='outlined'
                sx={{
                  height: '55px',
                  borderRadius: '4px',
                  padding: '0px 30px',
                }}
                onClick={() =>
                  navigate(`${SPARK_ROUTES.SPARK_BUILDER}/${sparkID}`)
                }
                startIcon={<EditIcon color='#6267AC' />}
              >
                <Typography
                  sx={{
                    fontSize: '18px',
                    textTransform: 'capitalize',
                  }}
                >
                  Edit Spark
                </Typography>
              </SparkButton>
            )}
            <SparkButton
              variant='contained'
              sx={{
                height: '55px',
                borderRadius: '4px',
                padding: '0px 30px',
              }}
              startIcon={<Star02 color='white' />}
              onClick={() => {
                if (isSparkRun) {
                  closeSparkRunDialog();
                  return;
                }
                navigate(`${SPARK_ROUTES.SPARK_RUNS}/${sparkID}`);
              }}
            >
              <Typography
                sx={{
                  fontSize: '18px',
                  textTransform: 'capitalize',
                }}
              >
                Run Spark
              </Typography>
            </SparkButton>
          </Box>
        </Box>
      )}
    </SparkDialog>
  );
}

export default RunSparkModal;
